import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit";
import { clearFilter } from "../library/greco-components/column-options/utils";
import { BooleanFilter, ExpandedColumn, NumberFilter } from "../types/columns";
import { RootState } from "./store";

export type RequireActionVehiclesColumnOptionsState = {
  columns: EntityState<ExpandedColumn>;
  tempColumns: EntityState<ExpandedColumn>;
  columnsCountryMap: Record<string, ExpandedColumn[]>;
  defaultColumnsCountryMap: Record<string, ExpandedColumn[]>;
  country: string;
  /** True when the working (temp) columns have unsaved edits relative to the
   * loaded view. Set on user edits, cleared when a view is (re)loaded or saved. */
  isDirty: boolean;
};

const columnsAdapter = createEntityAdapter<ExpandedColumn>({
  selectId: (c) => c.key,
});

export const requireActionVehiclesColumnOptionsSlice = createSlice({
  name: "ravColumnOptions",
  initialState: {
    columns: columnsAdapter.getInitialState(),
    tempColumns: columnsAdapter.getInitialState(),
    columnsCountryMap: {},
    defaultColumnsCountryMap: {},
    country: "",
    isDirty: false,
  },
  reducers: {
    setColumns: (
      s,
      a: PayloadAction<{
        columns: ExpandedColumn[];
        country: string;
      }>
    ) => {
      if (!s.columnsCountryMap[a.payload.country]) {
        columnsAdapter.setAll(s.columns, a.payload.columns);
        s.columnsCountryMap[a.payload.country] = s.columns;
        s.defaultColumnsCountryMap[a.payload.country] = s.columns;
      } else {
        s.columns = s.columnsCountryMap[a.payload.country];
      }
      s.tempColumns = s.columns;
      s.country = a.payload.country;
      s.isDirty = false;
    },
    updateColumns: (s, a: PayloadAction<ExpandedColumn[]>) => {
      columnsAdapter.setAll(s.columns, a.payload);
      s.columnsCountryMap[s.country] = s.columns;
    },
    setCountry: (s, a: PayloadAction<string>) => {
      s.country = a.payload;
      s.columns =
        s.columnsCountryMap[a.payload] || columnsAdapter.getInitialState();
    },
    setTempColumns: (s, a: PayloadAction<ExpandedColumn[]>) => {
      if (typeof a.payload !== "undefined") {
        columnsAdapter.setAll(s.tempColumns, a.payload);
        s.isDirty = false;
      }
    },
    toggleColumnVisibility: (s, a: PayloadAction<ExpandedColumn["key"]>) => {
      const col = s.tempColumns.entities[a.payload];
      if (!col) return;
      s.isDirty = true;
      const nowVisible = !col.isVisible;
      columnsAdapter.updateOne(s.tempColumns, {
        id: a.payload,
        changes: {
          isVisible: nowVisible,
          // A column removed from the visible fields must not keep filtering
          // the list, since the user can no longer see or edit its filter.
          ...(!nowVisible && col.filter
            ? { filter: clearFilter(col.filter) }
            : {}),
        },
      });
      // Newly selected columns move to the end of the order so they appear at
      // the bottom of the Selected pane (intuitive add behavior).
      if (nowVisible) {
        const idx = s.tempColumns.ids.indexOf(a.payload);
        if (idx !== -1) {
          s.tempColumns.ids.splice(idx, 1);
          s.tempColumns.ids.push(a.payload);
        }
      }
      s.columnsCountryMap[s.country] = s.columns;
    },
    showAllColumns: (s) => {
      s.isDirty = true;
      s.tempColumns.ids.forEach((id) => {
        s.tempColumns.entities[id].isVisible = true;
      });
      s.columnsCountryMap[s.country] = s.columns;
    },
    hideAllColumns: (s) => {
      s.isDirty = true;
      s.tempColumns.ids.forEach((id) => {
        const col = s.tempColumns.entities[id];
        if (!col.isFixed) {
          col.isVisible = false;
          if (col.filter) col.filter = clearFilter(col.filter);
        }
      });
      s.columnsCountryMap[s.country] = s.columns;
    },
    reorderColumn: (
      s,
      a: PayloadAction<{ index1: number; index2: number }>
    ) => {
      const { index1, index2 } = a.payload;
      s.isDirty = true;
      s.tempColumns.ids.splice(
        index2,
        0,
        s.tempColumns.ids.splice(index1, 1)[0]
      );
    },
    updateNumberFilter: (
      s,
      a: PayloadAction<{ key: ExpandedColumn["key"]; filter: NumberFilter }>
    ) => {
      const { key, filter } = a.payload;
      s.isDirty = true;
      const col = s.tempColumns.entities[key];
      if (col.filter && col.filter.type === "number") {
        col.filter = filter;
      }
    },
    setFilter: (s, a: PayloadAction<{ columnKey: string; filter: any }>) => {
      s.tempColumns.entities[a.payload.columnKey].filter = a.payload.filter;
      s.isDirty = true;
    },
    clearAllFilters: (
      s,
      a: PayloadAction<{
        key: ExpandedColumn["key"];
        value: BooleanFilter["value"];
      }>
    ) => {
      s.isDirty = true;
      s.tempColumns.ids.forEach((id) => {
        const col = s.tempColumns.entities[id];
        const defaultCol =
          s.defaultColumnsCountryMap[s.country].entities[col.key];
        col.filter = defaultCol?.filter;
      });
    },
    markSaved: (s) => {
      s.isDirty = false;
    },
  },
});

export const {
  actions: {
    setColumns: setRavPageColumns,
    setTempColumns: setRavPageTempColumns,
    updateColumns: updateRavPageColumns,
    setCountry: setRavPageColumnOptionsCountry,
    toggleColumnVisibility,
    showAllColumns,
    hideAllColumns,
    reorderColumn,
    updateNumberFilter,
    clearAllFilters,
    setFilter,
    markSaved: markRavPageColumnsSaved,
  },
  reducer: requireActionVehiclesColumnOptionsReducer,
} = requireActionVehiclesColumnOptionsSlice;

export const selectRavPageColumnsDirty = (s: RootState) =>
  s.requireActionVehiclesColumnOptions.isDirty;

export const { selectAll: selectAllRavPageColumns } =
  columnsAdapter.getSelectors<RootState>(
    (s) => s.requireActionVehiclesColumnOptions.columns
  );

export const { selectAll: selectAllRavPageTempColumns } =
  columnsAdapter.getSelectors<RootState>(
    (s) => s.requireActionVehiclesColumnOptions.tempColumns
  );

const localSelectors = columnsAdapter.getSelectors();

export const selectAreAllVisible = createSelector(
  (s) => localSelectors.selectAll(s.requireActionVehiclesColumnOptions.columns),
  (columns: any) => {
    return !columns.some((c) => !c.isVisible);
  }
);

export const selectAreColumnsDirty = createSelector(
  (s) => localSelectors.selectAll(s.requireActionVehiclesColumnOptions.columns),
  (localColumns) => (reduxColumns) => {
    return JSON.stringify(localColumns) !== JSON.stringify(reduxColumns);
  }
);

export const selectAreAnyFiltered = createSelector(
  (s) => localSelectors.selectAll(s.requireActionVehiclesColumnOptions.columns),
  (columns: any) => {
    return columns.some(
      (c) => c.filter && c.filter.isFiltered(c.filter as any)
    );
  }
);

export const selectColumnsTable = createSelector(
  (s) => s.requireActionVehiclesColumnOptions.columns,
  (s) => s.taxonomy,
  (s) => s.requireActionVehicles.sort,
  (columns, taxonomy, sort: any) => (t) => {
    const filteredTableColumns = (columns as any).ids
      ?.map((id) => (columns as any)?.entities[id])
      ?.filter((item) => item.isVisible && item.key !== "fleetName");
    const withTranslatedHeader = filteredTableColumns?.map((column: any) => {
      let iconName = column.iconName;
      if (column.key === sort.key && column.isSortable) {
        iconName =
          sort.key === column.key
            ? sort!.dir === "asc"
              ? "SortUp"
              : "SortDown"
            : "Sort";
      }
      return {
        ...column,
        name: t(column.name),
        iconName: iconName,
      };
    });
    return withTranslatedHeader;
    // return columns
    //   .filter((c) => c.isVisible && c.key !== "fleetName")
    //   .map((c) => {
    //     const dc = c.detailsListColumn
    //       ? c.detailsListColumn({ t, taxonomy, width: c.width })
    //       : null;
    //     return {
    //       key: c.key,
    //       width: c.width,
    //       label: t(c.labelKey),
    //       isSortable: c.isSortable ?? true,
    //       ...dc,
    //     } as DetailsListColumn;
    //   });
  }
);
